/**
 * ORTHO MODE (docs/MODELER.md "ORTHO MODE + GUIDE DISCIPLINE", Matt 2026-08-15).
 *
 * A STANDING toggle — not a per-gesture argument — constraining every draw/move
 * gesture to the ACTIVE SURFACE's cardinal axes. It lives here as module state
 * (set once from the app's persisted preference) rather than being threaded through
 * ResolveSnapArgs at ~60 call sites, because the spec's requirement is universal
 * coverage: draw tools, translate, node/edge grips, vertical (u, z) moves, 3D moves,
 * and detail drafting must ALL respect it, and a call site that forgot to pass a flag
 * would be a silent hole. Every gesture already funnels through `resolveSnap` /
 * `createSnapLock().resolve`, so consulting the mode there covers all of them at once.
 * `ResolveSnapArgs.ortho` remains as an explicit per-call override (tests; any gesture
 * that must never ortho).
 *
 * WHY CARDINAL NEEDS NO PER-SURFACE PLUMBING: the snap engine is coordinate-agnostic
 * 2D (snap.ts's own framing). Plan hands it world (x, y); a section/elevation hands it
 * (u, z); a detail hands its own sheet frame; a 3D move hands the working plane's
 * (x, y). So "the active surface's cardinal axes" IS [1,0] / [0,1] in whatever 2D frame
 * the caller already uses — the same reason Shift-lock and refGuides work unchanged on
 * every surface.
 *
 * SHIFT INVERTS (AutoCAD muscle memory). `free` (the shipped Shift meaning) is
 * reinterpreted while the mode is on: Shift held → resolve exactly as ortho-OFF would
 * with Shift NOT held. The standing Shift-locks-the-guide convention is explicitly
 * disabled under ortho by snapLock.ts's guard — guides are LIVE under ortho (ORTHO
 * GUIDES round, 2026-08-17: refGuides arms and offers axes, and resolveSnapOrtho
 * snaps where a guide crosses the cardinal constraint), so the guard is what keeps
 * the two Shift meanings from colliding.
 */
import type { Pt2 } from './types'

let orthoOn = false

/** Set the standing ortho mode (the app calls this from its persisted preference). */
export function setOrthoMode(on: boolean): void { orthoOn = !!on }

/** Is the standing ortho mode engaged? */
export function orthoMode(): boolean { return orthoOn }

/**
 * The cardinal constraint for a gesture running from `anchor` to `pt`, in whatever 2D
 * frame the caller uses. The axis is chosen by the DOMINANT delta (the CAD rule — it
 * flips live as the cursor crosses the diagonal), and `proj` is `pt` projected onto it.
 * Returns null when the cursor is still on the anchor (no direction yet).
 */
export function orthoConstrain(
  pt: Pt2, anchor: Pt2,
): { axis: { origin: Pt2; dir: Pt2 }; proj: Pt2 } | null {
  const dx = pt[0] - anchor[0], dy = pt[1] - anchor[1]
  if (Math.abs(dx) < 1e-9 && Math.abs(dy) < 1e-9) return null
  const horizontal = Math.abs(dx) >= Math.abs(dy)
  const dir: Pt2 = horizontal ? [1, 0] : [0, 1]
  const origin: Pt2 = [anchor[0], anchor[1]]
  const proj: Pt2 = horizontal ? [pt[0], anchor[1]] : [anchor[0], pt[1]]
  return { axis: { origin, dir }, proj }
}
